import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { infoNextLevel } from "../utils/levelSystem";
import {
  BookOpen,
  CreditCard,
  Edit3,
  Download,
  Library,
  Users,
  ChevronRight,
  GraduationCap,
  LogOut,
  Key,
  FileText,
  Shield,
  MessageCircle,
  Gamepad2,
  BarChart3,
  UserCheck,
} from "lucide-react";
import { styles } from "../utils/globalStyles";

export default function Dashboard({ user, onLogout }) {
  const role = user.role;
  const isAdmin = role === "admin";
  const isSiswa = role === "siswa";

  // STATE KHUSUS SISWA
  const [siswaStats, setSiswaStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  useEffect(() => {
    if (isSiswa) fetchSiswaStats();
  }, [user]);

  const fetchSiswaStats = async () => {
    setLoadingStats(true);
    const { data } = await supabase
      .from("siswa")
      .select("id, nama_lengkap, kelas, total_exp, level")
      .ilike("nama_lengkap", user.nama)
      .single();
    if (data) setSiswaStats(data);
    setLoadingStats(false);
  };

  const levelInfo = siswaStats
    ? infoNextLevel(siswaStats.total_exp)
    : { currentLevel: 1, progress: 0, targetExp: 100 };

  // --- HAK AKSES MENU ADMIN/GURU ---
  const canManageSiswa = ["operator", "kepala", "admin"].includes(role);
  const canManageKeuangan = ["operator", "kepala", "admin"].includes(role);
  const canManageMateri = ["operator", "kepala", "admin", "tutor"].includes(
    role
  );
  const canManageKuis = ["operator", "kepala", "admin", "tutor"].includes(role);
  const canManageTutor = ["kepala", "admin"].includes(role);
  const canWriteEvaluasi = ["tutor", "admin"].includes(role);
  const canManageMapel = ["admin", "kepala"].includes(role);
  const canManageGames = ["operator", "kepala", "admin", "tutor"].includes(
    role
  );

  // --- RENDER DASHBOARD SISWA (Layout Modern, Header Ramping) ---
  if (isSiswa) {
    return (
      <div
        style={{
          ...styles.container,
          background: "#F1F5F9",
          minHeight: "100vh",
        }}
      >
        {/* 1. HERO SECTION (LEBIH RAMPING) */}
        <div
          style={{
            background: "#1E293B",
            padding: "30px 20px 45px 20px", // Padding dikurangi agar lebih ramping
            borderRadius: "0 0 25px 25px",
            color: "white",
            boxShadow: "0 5px 20px rgba(30, 41, 59, 0.2)",
            position: "relative",
            marginBottom: "15px",
          }}
        >
          {/* Top Bar */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "start",
              marginBottom: 20,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "11px",
                  opacity: 0.8,
                  marginBottom: 2,
                  color: "#94A3B8",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                Selamat Datang,
              </div>
              <h1
                style={{
                  fontSize: "20px",
                  margin: 0,
                  fontWeight: "800",
                  color: "white",
                }}
              >
                {user.nama.split(" ")[0]}
              </h1>
              <div
                style={{
                  fontSize: "12px",
                  opacity: 0.9,
                  marginTop: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  color: "#CBD5E1",
                }}
              >
                {siswaStats ? `Kelas ${siswaStats.kelas}` : "..."}
              </div>
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <Link to="/ganti-password">
                <button
                  style={{
                    background: "rgba(255,255,255,0.1)",
                    border: "none",
                    padding: "8px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    color: "white",
                  }}
                >
                  <Key size={16} />
                </button>
              </Link>
              <button
                onClick={onLogout}
                style={{
                  background: "rgba(239, 68, 68, 0.2)",
                  border: "none",
                  padding: "8px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  color: "#FCA5A5",
                }}
              >
                <LogOut size={16} />
              </button>
            </div>
          </div>

          {/* Gamification Card (Lebih Compact) */}
          <div
            style={{
              background: "rgba(255, 255, 255, 0.05)",
              padding: "12px 15px",
              borderRadius: "12px",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            {/* Level Badge */}
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "2px solid #3B82F6",
                color: "white",
                fontWeight: "900",
                fontSize: "16px",
              }}
            >
              {levelInfo.currentLevel}
            </div>

            {/* Progress Bar */}
            <div style={{ flex: 1 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "11px",
                  marginBottom: 6,
                  fontWeight: "600",
                  color: "#E2E8F0",
                }}
              >
                <span>Level {levelInfo.currentLevel}</span>
                <span style={{ color: "#94A3B8" }}>
                  {siswaStats?.total_exp || 0} / {levelInfo.targetExp} EXP
                </span>
              </div>
              <div
                style={{
                  width: "100%",
                  background: "rgba(255,255,255,0.1)",
                  height: 6,
                  borderRadius: 10,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${levelInfo.progress}%`,
                    background: "#3B82F6",
                    height: "100%",
                    borderRadius: 10,
                    transition: "width 1s ease-out",
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* 2. MENU GRID SECTION */}
        <div style={{ padding: "0 20px 100px 20px", marginTop: -20 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "12px",
            }}
          >
            {/* Menu: Game & Misi */}
            <Link to="/game" style={{ textDecoration: "none" }}>
              <div
                style={{
                  background: "white",
                  padding: "15px",
                  borderRadius: "16px",
                  boxShadow: "0 4px 10px rgba(0,0,0,0.03)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  border: "1px solid #E2E8F0",
                  minHeight: 110,
                }}
              >
                <div
                  style={{
                    background: "#EFF6FF",
                    padding: 10,
                    borderRadius: "50%",
                    color: "#2563EB",
                  }}
                >
                  <Gamepad2 size={24} />
                </div>
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontWeight: "bold",
                      color: "#1E293B",
                      fontSize: "13px",
                    }}
                  >
                    Game & Misi
                  </div>
                  <div
                    style={{ fontSize: "10px", color: "#64748B", marginTop: 2 }}
                  >
                    Mainkan Sekarang
                  </div>
                </div>
              </div>
            </Link>

            {/* Menu: Materi */}
            <Link to="/materi" style={{ textDecoration: "none" }}>
              <div
                style={{
                  background: "white",
                  padding: "15px",
                  borderRadius: "16px",
                  boxShadow: "0 4px 10px rgba(0,0,0,0.03)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  minHeight: 110,
                }}
              >
                <div
                  style={{
                    background: "#F0F9FF",
                    padding: 10,
                    borderRadius: "50%",
                    color: "#0EA5E9",
                  }}
                >
                  <Library size={24} />
                </div>
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontWeight: "bold",
                      color: "#1E293B",
                      fontSize: "13px",
                    }}
                  >
                    Perpustakaan
                  </div>
                  <div
                    style={{ fontSize: "10px", color: "#64748B", marginTop: 2 }}
                  >
                    Baca Materi
                  </div>
                </div>
              </div>
            </Link>

            {/* Menu: Kuis */}
            <Link to="/kuis-siswa" style={{ textDecoration: "none" }}>
              <div
                style={{
                  background: "white",
                  padding: "15px",
                  borderRadius: "16px",
                  boxShadow: "0 4px 10px rgba(0,0,0,0.03)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  minHeight: 110,
                }}
              >
                <div
                  style={{
                    background: "#F5F3FF",
                    padding: 10,
                    borderRadius: "50%",
                    color: "#7C3AED",
                  }}
                >
                  <FileText size={24} />
                </div>
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontWeight: "bold",
                      color: "#1E293B",
                      fontSize: "13px",
                    }}
                  >
                    Ujian & Kuis
                  </div>
                  <div
                    style={{ fontSize: "10px", color: "#64748B", marginTop: 2 }}
                  >
                    Kerjakan Soal
                  </div>
                </div>
              </div>
            </Link>

            {/* Menu: Guru AI */}
            <Link to="/guru-ai" style={{ textDecoration: "none" }}>
              <div
                style={{
                  background: "white",
                  padding: "15px",
                  borderRadius: "16px",
                  boxShadow: "0 4px 10px rgba(0,0,0,0.03)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  minHeight: 110,
                }}
              >
                <div
                  style={{
                    background: "#DCFCE7",
                    padding: 10,
                    borderRadius: "50%",
                    color: "#16A34A",
                  }}
                >
                  <MessageCircle size={24} />
                </div>
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontWeight: "bold",
                      color: "#1E293B",
                      fontSize: "13px",
                    }}
                  >
                    Guru AI
                  </div>
                  <div
                    style={{ fontSize: "10px", color: "#64748B", marginTop: 2 }}
                  >
                    Tanya Jawab
                  </div>
                </div>
              </div>
            </Link>

            {/* Menu: Nilai */}
            <Link to="/nilai" style={{ textDecoration: "none" }}>
              <div
                style={{
                  background: "white",
                  padding: "15px",
                  borderRadius: "16px",
                  boxShadow: "0 4px 10px rgba(0,0,0,0.03)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  minHeight: 110,
                }}
              >
                <div
                  style={{
                    background: "#EFF6FF",
                    padding: 10,
                    borderRadius: "50%",
                    color: "#3B82F6",
                  }}
                >
                  <BarChart3 size={24} />
                </div>
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontWeight: "bold",
                      color: "#1E293B",
                      fontSize: "13px",
                    }}
                  >
                    Rapor Nilai
                  </div>
                  <div
                    style={{ fontSize: "10px", color: "#64748B", marginTop: 2 }}
                  >
                    Lihat Hasil
                  </div>
                </div>
              </div>
            </Link>

            {/* Menu: SPP */}
            <Link to="/tagihan-siswa" style={{ textDecoration: "none" }}>
              <div
                style={{
                  background: "white",
                  padding: "15px",
                  borderRadius: "16px",
                  boxShadow: "0 4px 10px rgba(0,0,0,0.03)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  minHeight: 110,
                }}
              >
                <div
                  style={{
                    background: "#FEF2F2",
                    padding: 10,
                    borderRadius: "50%",
                    color: "#EF4444",
                  }}
                >
                  <CreditCard size={24} />
                </div>
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontWeight: "bold",
                      color: "#1E293B",
                      fontSize: "13px",
                    }}
                  >
                    Tagihan SPP
                  </div>
                  <div
                    style={{ fontSize: "10px", color: "#64748B", marginTop: 2 }}
                  >
                    Cek Status
                  </div>
                </div>
              </div>
            </Link>

            {/* Menu: Daftar Tutor (PALING BAWAH, Full Width) */}
            <Link
              to="/list-tutor"
              style={{ textDecoration: "none", gridColumn: "1 / span 2" }}
            >
              <div
                style={{
                  background: "white",
                  padding: "15px 20px",
                  borderRadius: "16px",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
                  display: "flex",
                  alignItems: "center",
                  gap: 15,
                }}
              >
                <div
                  style={{
                    background: "#F0FDFA",
                    padding: 10,
                    borderRadius: "12px",
                    color: "#0D9488",
                  }}
                >
                  <UserCheck size={24} />
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontWeight: "bold",
                      color: "#334155",
                      fontSize: "14px",
                    }}
                  >
                    Daftar Tutor
                  </div>
                  <div style={{ fontSize: "11px", color: "#94A3B8" }}>
                    Lihat Pengajar Aktif
                  </div>
                </div>
                <ChevronRight size={20} color="#CBD5E1" />
              </div>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // --- RENDER DASHBOARD ADMIN/GURU (TETAP SAMA) ---
  return (
    <div style={styles.container}>
      <div
        style={{
          ...styles.headerHero,
          textAlign: "left",
          padding: "60px 25px 30px 25px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "start",
          }}
        >
          <div>
            <div
              style={{
                fontSize: "11px",
                opacity: 0.8,
                marginBottom: "4px",
                textTransform: "uppercase",
                letterSpacing: "1px",
              }}
            >
              Selamat Datang,
            </div>
            <h1 style={{ fontSize: "22px", margin: 0, fontWeight: "800" }}>
              {user.nama.split(" ")[0]}
            </h1>
            <div
              style={{
                marginTop: "6px",
                display: "inline-block",
                background: "rgba(255,255,255,0.2)",
                padding: "3px 8px",
                borderRadius: "6px",
                fontSize: "10px",
                fontWeight: "600",
                textTransform: "uppercase",
              }}
            >
              {role}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Link to="/ganti-password">
              <button
                style={{
                  background: "rgba(255,255,255,0.15)",
                  border: "none",
                  padding: "8px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  color: "white",
                }}
              >
                <Key size={16} />
              </button>
            </Link>
            <button
              onClick={onLogout}
              style={{
                background: "rgba(255,255,255,0.15)",
                border: "none",
                padding: "8px",
                borderRadius: "8px",
                cursor: "pointer",
                color: "#fca5a5",
              }}
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </div>

      <div
        style={{
          padding: "0 20px",
          position: "relative",
          zIndex: 10,
          marginTop: 20,
        }}
      >
        {isAdmin && (
          <div
            style={{
              ...styles.floatingCard,
              background: "#1E293B",
              color: "white",
              padding: "0",
              border: "none",
            }}
          >
            <Link
              to="/kelola-akun"
              style={{
                textDecoration: "none",
                color: "inherit",
                display: "flex",
                alignItems: "center",
                padding: "20px",
              }}
            >
              <div
                style={{
                  background: "#22C55E",
                  padding: "10px",
                  borderRadius: "10px",
                  marginRight: "15px",
                }}
              >
                <Shield size={20} color="white" />
              </div>
              <div style={{ flex: 1, textAlign: "left" }}>
                <div style={{ fontWeight: "bold", fontSize: "15px" }}>
                  Kelola Pengguna
                </div>
                <div style={{ fontSize: "12px", opacity: 0.7 }}>
                  Atur Akun & Keamanan
                </div>
              </div>
              <ChevronRight size={18} color="rgba(255,255,255,0.5)" />
            </Link>
          </div>
        )}

        <div style={isAdmin ? {} : styles.floatingCard}>
          <div
            style={{
              fontSize: "12px",
              fontWeight: "700",
              color: "#94a3b8",
              marginBottom: "15px",
              textTransform: "uppercase",
              letterSpacing: "1px",
              textAlign: "left",
            }}
          >
            Menu Manajemen
          </div>
          <div style={styles.gridContainer}>
            {canManageSiswa && (
              <Link to="/siswa" style={styles.gridItem}>
                <div
                  style={{
                    ...styles.gridIconBox,
                    background: "#EFF6FF",
                    color: "#3B82F6",
                  }}
                >
                  <Users size={20} />
                </div>
                <span style={{ fontSize: 11, fontWeight: 600 }}>Siswa</span>
              </Link>
            )}
            {canManageSiswa && (
              <Link to="/kelola-relasi" style={styles.gridItem}>
                <div
                  style={{
                    ...styles.gridIconBox,
                    background: "#E0F7FA",
                    color: "#006064",
                  }}
                >
                  <Users size={20} />
                </div>
                <span style={{ fontSize: 11, fontWeight: 600 }}>Kelas</span>
              </Link>
            )}
            {canManageMapel && (
              <Link to="/kelola-mapel" style={styles.gridItem}>
                <div
                  style={{
                    ...styles.gridIconBox,
                    background: "#FEF2F2",
                    color: "#EF4444",
                  }}
                >
                  <BookOpen size={20} />
                </div>
                <span style={{ fontSize: 11, fontWeight: 600 }}>Mapel</span>
              </Link>
            )}
            {canManageKeuangan && (
              <Link to="/tagihan-admin" style={styles.gridItem}>
                <div
                  style={{
                    ...styles.gridIconBox,
                    background: "#F0FDF4",
                    color: "#22C55E",
                  }}
                >
                  <CreditCard size={20} />
                </div>
                <span style={{ fontSize: 11, fontWeight: 600 }}>SPP</span>
              </Link>
            )}
            {canManageTutor && (
              <Link to="/kelola-tutor" style={styles.gridItem}>
                <div
                  style={{
                    ...styles.gridIconBox,
                    background: "#ECFEFF",
                    color: "#06B6D4",
                  }}
                >
                  <GraduationCap size={20} />
                </div>
                <span style={{ fontSize: 11, fontWeight: 600 }}>Tutor</span>
              </Link>
            )}
            {canManageKuis && (
              <Link to="/kelola-kuis" style={styles.gridItem}>
                <div
                  style={{
                    ...styles.gridIconBox,
                    background: "#F5F3FF",
                    color: "#7C3AED",
                  }}
                >
                  <FileText size={20} />
                </div>
                <span style={{ fontSize: 11, fontWeight: 600 }}>Kuis</span>
              </Link>
            )}
            {canManageGames && (
              <Link to="/kelola-gamifikasi" style={styles.gridItem}>
                <div
                  style={{
                    ...styles.gridIconBox,
                    background: "#E0E7FF",
                    color: "#4338CA",
                  }}
                >
                  <Gamepad2 size={20} />
                </div>
                <span style={{ fontSize: 11, fontWeight: 600 }}>Game</span>
              </Link>
            )}
            {canManageMateri && (
              <Link to="/kelola-materi" style={styles.gridItem}>
                <div
                  style={{
                    ...styles.gridIconBox,
                    background: "#FAF5FF",
                    color: "#A855F7",
                  }}
                >
                  <Library size={20} />
                </div>
                <span style={{ fontSize: 11, fontWeight: 600 }}>Materi</span>
              </Link>
            )}
            {canWriteEvaluasi && (
              <Link to="/tulis-evaluasi" style={styles.gridItem}>
                <div
                  style={{
                    ...styles.gridIconBox,
                    background: "#FFF7ED",
                    color: "#F97316",
                  }}
                >
                  <Edit3 size={20} />
                </div>
                <span style={{ fontSize: 11, fontWeight: 600 }}>Evaluasi</span>
              </Link>
            )}
          </div>
        </div>

        <div style={{ marginTop: "25px", paddingBottom: 50 }}>
          <div
            style={{
              fontSize: "12px",
              fontWeight: "700",
              color: "#94a3b8",
              marginBottom: "15px",
              textTransform: "uppercase",
              letterSpacing: "1px",
              textAlign: "left",
            }}
          >
            Akses Lainnya
          </div>
          <div style={styles.listCard}>
            <Link to="/guru-ai" style={styles.listItem}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    marginRight: 12,
                    background: "#FEF9C3",
                    color: "#CA8A04",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <MessageCircle size={16} />
                </div>
                <span style={{ fontSize: 13, fontWeight: 600 }}>
                  Tanya Miss Nami (AI)
                </span>
              </div>
              <ChevronRight size={16} color="#CBD5E1" />
            </Link>
            <Link
              to="/materi"
              style={{ ...styles.listItem, borderBottom: "none" }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    marginRight: 12,
                    background: "#FFF1F2",
                    color: "#F43F5E",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Download size={16} />
                </div>
                <span style={{ fontSize: 13, fontWeight: 600 }}>
                  Materi Belajar
                </span>
              </div>
              <ChevronRight size={16} color="#CBD5E1" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
